import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React from 'react'
import { useSelector } from 'react-redux'
import { selectApp } from 'selectors/app'
import { history } from 'store'
import AppNavigationTabs from '../AppNavigation/Tabs'
import { ContentNavigation, SubTab, Tab as NavTab } from './ContentNavigation'

const t = buildTranslate('cms')

const ContentTabsLayout: React.FC = () => {
  const { app_ordering_enabled, branded_app } = useCurrentMerchant() || {}
  const app = useSelector(selectApp)
  const brandedAppAvailable = app?.allow_downloads && branded_app
  const appWithOrdering = app_ordering_enabled && brandedAppAvailable
  const customNonOrderingHomePageEnabled = useFlag(
    'non-ordering-custom-home-page-mx',
    false
  )

  // Helper function to determine if a tab should be active
  const isTabActive = (tabKey: string) => {
    const pathname = history.location.pathname
    const hash = history.location.hash

    // AppNavigation routes are handled internally by AppNavigationTabs component
    if (pathname.startsWith('/brand_content/app_navigation')) {
      return false
    }

    // Regular content routes (hash-based)
    if (pathname === '/brand_content') {
      return hash.replace('#', '') === tabKey
    }

    return false
  }

  return (
    <ContentNavigation>
      {/* App home page tabs (conditional) - hardcoded, no hooks */}
      {(appWithOrdering || customNonOrderingHomePageEnabled) && (
        <NavTab
          tabKey="content"
          name={t('content.app_home_page.tab_title')}
          active={isTabActive('content')}
        />
      )}

      {/* App home page with subtabs (conditional) - hardcoded, no hooks */}
      {brandedAppAvailable &&
        !appWithOrdering &&
        !customNonOrderingHomePageEnabled && (
          <NavTab
            tabKey="hero_carousel"
            name={t('landing.tabs.home_page')}
            active={isTabActive('hero_carousel')}
          >
            <SubTab
              tabKey="hero_carousel"
              name={t('content.hero_carousel.tab_title')}
              active={isTabActive('hero_carousel')}
            />
            <SubTab
              tabKey="content_blocks"
              name={t('content.content_blocks.tab_title')}
              active={isTabActive('content_blocks')}
            />
            <SubTab
              tabKey="fixed_buttons"
              name={t('content.fixed_buttons.tab_title')}
              active={isTabActive('fixed_buttons')}
            />
          </NavTab>
        )}

      {/* App Navigation tab with subtabs - conditionally rendered based on visibility flags */}
      <AppNavigationTabs />

      {/* Menu category tab */}
      <NavTab
        tabKey="menu_category"
        name={t('content.menu_category.tab_title')}
        active={isTabActive('menu_category')}
      />

      {/* Web hero image tab */}
      <NavTab
        tabKey="web_hero_image"
        name={t('content.web_hero_image.title')}
        active={isTabActive('web_hero_image')}
      />

      {/* Banner tabs */}
      {appWithOrdering && (
        <>
          <NavTab
            tabKey="location_banner"
            name={t('content.banners.location_banner.subtab_title')}
            active={isTabActive('location_banner')}
          />
          <NavTab
            tabKey="order_placed_banner"
            name={t('content.banners.order_placed_banner.subtab_title')}
            active={isTabActive('order_placed_banner')}
          />
          <NavTab
            tabKey="error_banner"
            name={t('content.banners.error_banner.subtab_title')}
            active={isTabActive('error_banner')}
          />
        </>
      )}

      {/* Alert banner (non-ordering) */}
      {!appWithOrdering && (
        <NavTab
          tabKey="alert_banner"
          name={t('content.alert_banner.title')}
          active={isTabActive('alert_banner')}
        />
      )}

      {/* Custom links (conditional) */}
      {brandedAppAvailable && (
        <NavTab
          tabKey="custom_links"
          name={t('content.custom_links.title')}
          active={isTabActive('custom_links')}
        />
      )}
    </ContentNavigation>
  )
}

export default ContentTabsLayout
